import { useEffect, useState } from 'react'
import { adminAPI } from '../services/api'
import { apiErrorMessage } from '../utils/apiError'
import { formatDeciderTime } from './trackRecord/Phase2DeciderNote'
import type { ShadowBucket, ShadowFiltersReport, ShadowRule } from '../types/shadowFilters'

/**
 * Settings → Engine, admins only: two candidate rules recorded on every
 * engine signal and never applied (backend services/shadow_filters). Each
 * closed signal lands in a rule's "kept" or "would skip" row; net is after
 * the platform fee on entry and exit. Rendered as the server sends it.
 */

export const NOT_ENOUGH_TEXT = 'Not enough closed signals yet'

const RULES: { key: ShadowRule; title: string }[] = [
  { key: 'no_short_btc_uptrend', title: 'A — No shorts while BTC’s 30-day return is positive' },
  { key: 'skip_ranging', title: 'C — Skip signals labelled “ranging”' },
  { key: 'both', title: 'Both rules' },
]

const pct = (v: number | null) => (v === null ? '—' : `${(v * 100).toFixed(1)}%`)
const signedPct = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`
const day = (iso: string) => (formatDeciderTime(iso) ?? iso).replace(/ \d{2}:\d{2} UTC$/, '')

const Row = ({ label, s, testId }: { label: string; s: ShadowBucket; testId: string }) => (
  <tr className="border-t border-gray-100" data-testid={testId}>
    <td className="px-2 py-1.5 text-gray-700">{label}</td>
    <td className="px-2 py-1.5 text-right tabular-nums">{s.n}</td>
    <td className="px-2 py-1.5 text-right tabular-nums">{pct(s.win_rate)}</td>
    <td className="px-2 py-1.5 text-right tabular-nums">{s.n ? signedPct(s.sum_net_pct) : '—'}</td>
  </tr>
)

const RuleTable = ({ report, rule }: { report: ShadowFiltersReport; rule: ShadowRule }) => {
  const stats = report.rules[rule]
  return (
    <table className="w-full text-sm" data-testid={`shadow-${rule}`}>
      <thead>
        <tr className="text-xs text-gray-500">
          <th className="px-2 py-1 text-left font-medium">Verdict</th>
          <th className="px-2 py-1 text-right font-medium">n</th>
          <th className="px-2 py-1 text-right font-medium">Win rate</th>
          <th className="px-2 py-1 text-right font-medium">Net after fees</th>
        </tr>
      </thead>
      <tbody>
        <Row label="Kept" s={stats.kept} testId={`shadow-${rule}-kept`} />
        <Row label="Would skip" s={stats.would_skip} testId={`shadow-${rule}-skip`} />
        {stats.unknown.n > 0 && <Row label="Unknown (no BTC data)" s={stats.unknown} testId={`shadow-${rule}-unknown`} />}
      </tbody>
    </table>
  )
}

const ShadowFiltersPanel = () => {
  const [data, setData] = useState<ShadowFiltersReport | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    adminAPI
      .shadowFilters()
      .then((d) => live && setData(d))
      .catch((e) => live && setError(apiErrorMessage(e, 'Could not load the shadow filters')))
    return () => {
      live = false
    }
  }, [])

  return (
    <section
      className="rounded-lg border border-gray-200 bg-white p-4"
      aria-label="Shadow filters (record only)"
      data-testid="shadow-filters"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-900">Shadow filters (record only)</h2>
        <span className="text-xs text-gray-400">Admin only</span>
      </div>

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      {!data && !error && <p className="mt-2 text-sm text-gray-500">Loading…</p>}

      {data && (
        <div className="mt-2 space-y-4 text-sm">
          <p className="text-gray-700" data-testid="shadow-since">
            {data.first_recorded_at
              ? `Recorded from ${day(data.first_recorded_at)}. These rules don't change any signal. Judged only on signals closed after that date.`
              : "Nothing recorded yet. These rules don't change any signal."}
          </p>

          {data.closed < data.min_closed ? (
            <p className="text-gray-600" data-testid="shadow-not-enough">
              {NOT_ENOUGH_TEXT} ({data.closed} of {data.min_closed}).
            </p>
          ) : (
            RULES.map(({ key, title }) => (
              <div key={key}>
                <h3 className="font-medium text-gray-800">{title}</h3>
                <RuleTable report={data} rule={key} />
              </div>
            ))
          )}

          <p className="text-xs text-gray-400">
            Closed engine signals in the record that carry a shadow-filter record. Win rate is wins over all closes; net
            is the summed percent move after a {+data.round_trip_cost_pct.toFixed(2)}% round trip a trade (the platform
            fee on entry and exit).
          </p>
        </div>
      )}
    </section>
  )
}

export default ShadowFiltersPanel
